import type { OrpcClient } from '@/orpc/client'

// Wire shapes come from the oRPC contract, never re-declared here.
type Out<K extends keyof OrpcClient> = Awaited<ReturnType<OrpcClient[K]>>

export type ReferralSpecialtyCode = Parameters<
  OrpcClient['referralDiscoveryNearby']
>[0]['specialtyCode']
export type ReferralLocation = Out<'referralDiscoveryNearby'>['locations'][number]
export type RosterView = ReferralLocation['roster']
export type RosterDoctor = RosterView['doctors'][number]
export type ReferralDoctor = Out<'referralDiscoveryDoctors'>['doctors'][number]
export type FrequentList = Out<'referralDiscoveryFrequent'>
export type FrequentRecipient = FrequentList['recipients'][number]
export type ResolvedRecipient = Out<'referralDiscoveryResolve'>
export type FaxCandidate = ResolvedRecipient['faxCandidates'][number]
export type FaxSource = FaxCandidate['source']
export type ReferralDraftInput = Parameters<OrpcClient['referralDraft']>[0]

/** The recipient a specialist referral goes to, as the form holds it. */
export type SelectedRecipient = {
  npi?: string
  placeId?: string
  firstName?: string
  lastName?: string
  credential?: string
  practiceName?: string
  specialtyLabel: string
  street: string
  city: string
  state: string
  zip: string
  phone?: string
  /** The fax the clinician confirmed — the number the referral is sent to. */
  fax: string
  faxCandidates: FaxCandidate[]
  /** Typed in by hand rather than found. */
  manual?: boolean
}

export const FAX_SOURCE_LABEL: Record<FaxSource, string> = {
  saved: 'Used before',
  'directory:cms': 'CMS directory',
  'directory:humana': 'Humana directory',
  nppes: 'NPPES',
  website: 'Practice website',
}

export function recipientName(
  r: Pick<SelectedRecipient, 'firstName' | 'lastName' | 'credential' | 'practiceName'>,
): string {
  if (r.lastName) {
    return [[r.firstName, r.lastName].filter(Boolean).join(' '), r.credential]
      .filter(Boolean)
      .join(', ')
  }
  return r.practiceName ?? 'Unnamed provider'
}

const nameKey = (s: string) =>
  s
    .toLowerCase()
    .replace(/^dr\.?\s+/, '')
    .replace(/[^a-z0-9]/g, '')

/**
 * The practice to show under a named doctor, or undefined when there is none or it is just the
 * doctor's own name (Google often lists a solo practice under the doctor's name).
 */
export function distinctPracticeName(
  r: Pick<SelectedRecipient, 'firstName' | 'lastName' | 'credential' | 'practiceName'>,
): string | undefined {
  if (!r.lastName || !r.practiceName) return undefined
  const practice = nameKey(r.practiceName)
  const doctor = [r.firstName, r.lastName].filter(Boolean).join(' ')
  return practice === nameKey(recipientName(r)) || practice === nameKey(doctor)
    ? undefined
    : r.practiceName
}

export function isValidFax(fax: string): boolean {
  const digits = fax.replace(/\D/g, '')
  return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'))
}

/**
 * The key a row is matched against Frequently used by. The server keeps one record per
 * doctor-office, so a clinic's roster doctor is remembered at that clinic only; a doctor
 * found by name (no place) and a practice are keyed by themselves.
 */
export function rememberKey(r: { npi?: string; placeId?: string }): string {
  if (!r.npi) return `place:${r.placeId}`
  return r.placeId ? `npi:${r.npi}@${r.placeId}` : `npi:${r.npi}`
}

export function toSelectedRecipient(resolved: ResolvedRecipient): SelectedRecipient {
  return {
    npi: resolved.npi,
    placeId: resolved.placeId,
    firstName: resolved.firstName,
    lastName: resolved.lastName,
    credential: resolved.credential,
    practiceName: resolved.practiceName,
    specialtyLabel: resolved.specialtyLabel,
    street: resolved.street,
    city: resolved.city,
    state: resolved.state,
    zip: resolved.zip,
    phone: resolved.phone,
    fax: resolved.recommendedFax ?? '',
    faxCandidates: resolved.faxCandidates,
  }
}

/** `formData.recipient` for /order/create (validated server-side by RecipientInputSchema). */
export function recipientFormData(
  r: SelectedRecipient,
  specialty: { code: string; label: string },
) {
  return {
    npi: r.npi,
    placeId: r.placeId,
    name: recipientName(r),
    firstName: r.firstName,
    lastName: r.lastName,
    credential: r.credential,
    practiceName: r.practiceName,
    specialtyCode: specialty.code,
    specialtyLabel: specialty.label,
    street: r.street,
    city: r.city,
    state: r.state,
    zip: r.zip,
    phone: r.phone,
    fax: r.fax,
  }
}
